import { jsx as _jsx } from "react/jsx-runtime";
/**
 * Impersonation overlay context — injected by app-level providers into SDK
 * auth hooks so the SDK doesn't import any app-specific Zustand stores or
 * impersonation libraries.
 *
 * Platform-neutral: pure React context + TypeScript interfaces. No web/RN
 * globals, no vendor imports beyond React. Safe in `src/auth/` core per
 * `.claude/rules/platform-agnostic.md`.
 *
 * Wiring: the frontend `ImpersonationProvider` (or an equivalent adapter)
 * renders an `<ImpersonationOverlayProvider>` that supplies the current
 * impersonation state. When no provider is mounted, all hooks behave as if
 * impersonation is inactive (`isActive: false`, `target: null`).
 */
import { createContext, useContext, useState } from 'react';
const DEFAULT_OVERLAY = Object.freeze({
    isActive: false,
    target: null,
});
// ── Context ──────────────────────────────────────────────────────────────────
/** Reads the current impersonation overlay. Defaults to inactive when no
 *  provider is mounted above the call site. */
const ImpersonationOverlayContext = createContext(DEFAULT_OVERLAY);
/** Returns the current impersonation overlay. */
export function useImpersonationOverlay() {
    return useContext(ImpersonationOverlayContext);
}
/**
 * Mounts the impersonation overlay into the SDK auth hook tree.
 *
 * The frontend wires this once, just inside `ImpersonationProvider`, passing
 * the Zustand store's `{ isActive, target }` slice:
 *
 * ```tsx
 * // In the frontend adapter:
 * const isActive = useImpersonationStore((s) => s.isActive);
 * const target = useImpersonationStore((s) => s.target);
 * return (
 *   <ImpersonationOverlayProvider value={{ isActive, target }}>
 *     {children}
 *   </ImpersonationOverlayProvider>
 * );
 * ```
 */
export function ImpersonationOverlayProvider({ value, children, }) {
    return (_jsx(ImpersonationOverlayContext.Provider, { value: value, children: children }));
}
/**
 * Self-contained state hook for test environments (no external store needed).
 * Do NOT use in production app code — wire the Zustand store instead.
 */
export function useImpersonationOverlayState(initial = DEFAULT_OVERLAY) {
    const [overlay, setOverlay] = useState(initial);
    return { overlay, setOverlay };
}
//# sourceMappingURL=auth-overlay-context.js.map